import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { renewalAPI, settingsAPI, customerAPI } from '../services/api';
import { format, differenceInDays, addDays, setHours, startOfHour } from 'date-fns';
import toast from 'react-hot-toast';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Phone, Search, MessageCircle, Wifi, Tv, PhoneMissed, Package, QrCode, MapPin, Globe, StickyNote, AlarmClock, X, CalendarDays } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';
import Num from '../components/bits/Num';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const DEFAULT_TEMPLATE_BODY = `Dear {{name}}, your broadband subscription expires on {{expiry}}. Please renew to continue uninterrupted service. Send "HI" to receive our latest plans.`;
const DEFAULT_TEMPLATES = [{ name: 'Standard Renewal', body: DEFAULT_TEMPLATE_BODY, isDefault: true }];

function WaDropdown({ customer, templates }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!customer.mobile) return null;

  const send = async (templateIndex) => {
    setOpen(false);
    setSending(true);
    try {
      const { data } = await customerAPI.sendReminder(customer._id, templateIndex);
      toast.success(data.message || 'Reminder sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reminder failed');
    } finally {
      setSending(false);
    }
  };

  if (templates.length === 1) {
    return (
      <button onClick={() => send(0)} disabled={sending}
        className="btn btn-sm" title={templates[0].name}
        style={{ background: 'var(--cb-25d366)', borderColor: 'var(--cb-25d366)', color: 'var(--c-ffffff)', padding: '3px 8px' }}>
        <MessageCircle size={13} />
      </button>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-sm" title="Choose template & send WhatsApp" disabled={sending}
        style={{ background: 'var(--cb-25d366)', borderColor: 'var(--cb-25d366)', color: 'var(--c-ffffff)', padding: '3px 8px' }}
        onClick={() => setOpen(o => !o)}>
        <MessageCircle size={13} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 200,
          background: 'var(--card-bg, #fff)', border: '1px solid var(--border)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 200,
        }}>
          {templates.map((tpl, i) => (
            <button key={i}
              onClick={() => send(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                padding: '9px 12px', fontSize: '.83rem', color: 'var(--text)', textAlign: 'left',
                borderBottom: i < templates.length - 1 ? '1px solid var(--border)' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <MessageCircle size={12} color="var(--c-25d366)" style={{ flexShrink: 0 }} />
              {tpl.name}
              {tpl.isDefault && <span style={{ marginLeft: 'auto', fontSize: '.7rem', color: 'var(--c-25d366)', fontWeight: 700 }}>Default</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// One-click MessageTemplate send (Call Couldn't Connect / Send Plans / Send QR) — a
// no-picker sibling to WaDropdown above: each button always sends the one active template
// configured for its fixed category (Message Center → category), no choice needed.
function TemplateActionButton({ customer, category, title, color, icon: Icon }) {
  const [sending, setSending] = useState(false);
  if (!customer.mobile) return null;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await customerAPI.sendTemplate(customer._id, category);
      toast.success(data.message || 'Sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <button onClick={send} disabled={sending} className="btn btn-sm" title={title}
      style={{ background: color, borderColor: color, color: 'var(--c-ffffff)', padding: '3px 8px' }}>
      <Icon size={13} />
    </button>
  );
}

// Keys mirror backend RenewalNote OUTCOMES.
const OUTCOMES = [
  { key: 'will_pay', label: 'Will pay / renew', color: 'var(--success)' },
  { key: 'promised_date', label: 'Promised date', color: 'var(--primary)' },
  { key: 'no_answer', label: 'Call not answered', color: 'var(--c-ea580c)' },
  { key: 'unreachable', label: 'Switched off / unreachable', color: 'var(--c-ea580c)' },
  { key: 'not_interested', label: 'Not interested / disconnect', color: 'var(--danger)' },
  { key: 'other', label: 'Other', color: 'var(--text-muted)' },
];
const outcomeOf = key => OUTCOMES.find(o => o.key === key) || OUTCOMES[OUTCOMES.length - 1];
const DT_INPUT = "yyyy-MM-dd'T'HH:mm";

function ReminderBadge({ reminder }) {
  if (!reminder?.remindAt) return null;
  const sent = reminder.status === 'sent';
  return (
    <span title={sent ? 'Reminder sent' : 'Reminder pending'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: sent ? 'var(--text-muted)' : 'var(--primary)', fontWeight: 600 }}>
      <AlarmClock size={10} /> {format(new Date(reminder.remindAt), 'dd/MM h:mm a')}{sent ? ' (sent)' : ''}
    </span>
  );
}

// Latest call note under the customer's name on the dashboard row.
function LatestNoteLine({ note }) {
  if (!note) return null;
  const o = outcomeOf(note.outcome);
  return (
    <div style={{ fontSize: '.72rem', marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ color: o.color, fontWeight: 700 }}>{o.label}</span>
      {note.text && <span style={{ color: 'var(--text)' }}>{note.text}</span>}
      <span style={{ color: 'var(--text-muted)' }}>— {note.createdBy?.name || 'Staff'}, {format(new Date(note.createdAt), 'dd/MM h:mm a')}</span>
      <ReminderBadge reminder={note.reminderNote} />
    </div>
  );
}

// Call history + "add note" form for one customer's service. A follow-up reminder also
// lands in the Notes section (backend creates a personal Note), which WhatsApps it at that time.
function RenewalNoteModal({ customer, kind, onClose, onAdded }) {
  const [history, setHistory] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [text, setText] = useState('');
  const [remind, setRemind] = useState(false);
  const [remindAt, setRemindAt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    renewalAPI.getNotes(customer._id, kind)
      .then(({ data }) => setHistory(data.data))
      .catch(() => { setHistory([]); toast.error('Failed to load notes'); });
  }, [customer._id, kind]);

  const quick = (days) => setRemindAt(format(startOfHour(setHours(addDays(new Date(), days), 10)), DT_INPUT));

  const save = async (e) => {
    e.preventDefault();
    if (!outcome) { toast.error('Pick what happened on the call'); return; }
    if (outcome === 'other' && !text.trim()) { toast.error('Write a note for "Other"'); return; }
    if (remind && !remindAt) { toast.error('Pick a reminder time'); return; }
    setSaving(true);
    try {
      const { data } = await renewalAPI.addNote({
        customerId: customer._id, serviceType: kind, outcome, text: text.trim(),
        remindAt: remind ? new Date(remindAt).toISOString() : undefined,
      });
      toast.success(remind ? 'Note saved, reminder added to Notes' : 'Note saved');
      onAdded(data.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>Call Notes — {customer.name} ({kind === 'broadband' ? 'Broadband' : 'CableTV'})</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">What happened? <span className="required">*</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {OUTCOMES.map(o => (
                  <button type="button" key={o.key} onClick={() => setOutcome(o.key)} className="btn btn-sm"
                    style={outcome === o.key ? { background: o.color, borderColor: o.color, color: 'var(--c-ffffff)' } : {}}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Note {outcome === 'other' && <span className="required">*</span>}</label>
              <textarea className="form-control" rows={2} value={text} onChange={e => setText(e.target.value)}
                placeholder="e.g. will pay on Friday after salary" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={remind} onChange={e => { setRemind(e.target.checked); if (e.target.checked && !remindAt) quick(1); }} />
                <AlarmClock size={14} /> Set follow-up reminder (goes to Notes + WhatsApp)
              </label>
              {remind && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="btn btn-sm" onClick={() => quick(1)}>Tomorrow 10 AM</button>
                  <button type="button" className="btn btn-sm" onClick={() => quick(2)}>In 2 days</button>
                  <input type="datetime-local" className="form-control" style={{ width: 210, padding: '4px 8px' }}
                    value={remindAt} min={format(new Date(), DT_INPUT)} onChange={e => setRemindAt(e.target.value)} />
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div className="form-label" style={{ fontWeight: 600 }}>History</div>
              {history === null ? (
                <div className="spinner" style={{ margin: '8px auto' }} />
              ) : history.length === 0 ? (
                <div className="text-muted text-small">No calls logged yet.</div>
              ) : (
                <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {history.map(n => (
                    <div key={n._id} style={{ fontSize: '.8rem', paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: outcomeOf(n.outcome).color, fontWeight: 700 }}>{outcomeOf(n.outcome).label}</span>
                      {n.text && <span> — {n.text}</span>}
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span>{n.createdBy?.name || 'Staff'}, {format(new Date(n.createdAt), 'dd/MM/yyyy h:mm a')}</span>
                        <ReminderBadge reminder={n.reminderNote} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Note'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Same send as Customer Detail → App Info's "Share on WhatsApp": the server builds the
// portal_login template + link and sends it through the company WhatsApp gateway.
function PortalLinkButton({ customer }) {
  const [sending, setSending] = useState(false);
  if (!customer.mobile) return null;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await customerAPI.sendPortalLogin(customer._id);
      toast.success(data.message || 'Login link sent on WhatsApp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send on WhatsApp');
    } finally {
      setSending(false);
    }
  };

  return (
    <button onClick={send} disabled={sending} className="btn btn-sm" title="Send Portal Link"
      style={{ background: 'var(--cb-2563eb)', borderColor: 'var(--cb-2563eb)', color: 'var(--c-ffffff)', padding: '3px 8px' }}>
      <Globe size={13} />
    </button>
  );
}

function ExpiryChip({ date }) {
  if (!date) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const d = differenceInDays(new Date(date), new Date());
  if (d < 0) return <span className="badge badge-danger">Expired {Math.abs(d)}d ago</span>;
  if (d === 0) return <span className="badge badge-danger">Today</span>;
  if (d <= 3) return <span className="badge badge-warning">{d}d left</span>;
  if (d <= 7) return <span className="badge badge-info">{d}d left</span>;
  return <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{format(new Date(date), 'dd/MM/yyyy')}</span>;
}

// Current paid period for a service — read off the customer's most recent renewal
// payment (see backend attachValidity()), not plan.validityDays, since multi-month/
// pricing-tier renewals mean the actual paid window often isn't a single plan cycle.
function ValidityLine({ service }) {
  if (!service?.validityFrom || !service?.validityTo) return null;
  const days = differenceInDays(new Date(service.validityTo), new Date(service.validityFrom)) + 1;
  return (
    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
      {format(new Date(service.validityFrom), 'dd/MM/yyyy')} – {format(new Date(service.validityTo), 'dd/MM/yyyy')} ({days}d)
    </div>
  );
}

// One service's expiring-customer table — used twice (Broadband, CableTV) so each
// section only lists customers whose *own* service expiry falls in the selected window.
function ServiceSection({ icon: Icon, color, title, rows, kind, waTemplates, onNoteAdded }) {
  const [noteFor, setNoteFor] = useState(null);
  const isApp = useViewMode().viewMode === 'app';
  const [moreFor, setMoreFor] = useState(null); // App view: customer whose extra actions are open
  // One action = the existing button + a plain-words caption (App view "More" panel).
  const withCaption = (text, node) => (
    <div>{node}<span>{text}</span></div>
  );
  const appCard = c => {
    const expiry = kind === 'broadband' ? c.broadband?.expiryDate : c.catv?.expiryDate;
    const plan = kind === 'broadband' ? c.broadband?.plan?.name : c.catv?.package?.name;
    const open = moreFor === c._id;
    return (
      <div key={c._id} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to={`/customers/${c._id}`} style={{ flex: 1, minWidth: 0, fontWeight: 700, color: 'var(--text)', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</Link>
          <ExpiryChip date={expiry} />
        </div>
        <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span>{c.mobile}</span>
          {plan && <span>{plan}</span>}
          {c.outstandingBalance > 0 && <strong style={{ color: 'var(--danger)' }}>₹{fmt(c.outstandingBalance)} due</strong>}
        </div>
        <LatestNoteLine note={c.renewalNote} />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 2 }}>
          <Link to={`/customers/${c._id}?tab=collect`} className="btn btn-primary btn-sm" style={{ minHeight: 34, padding: '0 14px', fontSize: '.8rem' }}>
            <RefreshCw size={13} /> Renew
          </Link>
          {c.mobile && (
            <a href={`tel:${c.mobile}`} aria-label="Call" className="btn btn-secondary btn-sm" style={{ minHeight: 34, minWidth: 38, padding: 0, justifyContent: 'center' }}><Phone size={14} /></a>
          )}
          <button type="button" aria-label="More actions" className="btn btn-secondary btn-sm" aria-expanded={open}
            style={{ minHeight: 34, minWidth: 38, padding: 0, justifyContent: 'center' }}
            onClick={() => setMoreFor(open ? null : c._id)}>{open ? '▴' : '⋯'}</button>
        </div>
        {open && (
          <div className="rn-more">
            {withCaption('Notes', (
              <button className="btn btn-sm" onClick={() => setNoteFor(c)}
                style={{ background: 'var(--cb-ca8a04)', borderColor: 'var(--cb-ca8a04)', color: 'var(--c-ffffff)', padding: '3px 8px' }}>
                <StickyNote size={13} />
              </button>
            ))}
            {withCaption('WhatsApp', <WaDropdown customer={c} templates={waTemplates} />)}
            {withCaption('No answer', <TemplateActionButton customer={c} category="call_no_answer" title="Call Couldn't Connect" color="var(--cb-ea580c)" icon={PhoneMissed} />)}
            {withCaption('Send plans', <TemplateActionButton customer={c} category="plans" title="Send Plans" color="var(--cb-0d9488)" icon={Package} />)}
            {withCaption('Send QR', <TemplateActionButton customer={c} category="payment_qr" title="Send QR" color="var(--cb-111827)" icon={QrCode} />)}
            {kind === 'broadband' && withCaption('QR 2', <TemplateActionButton customer={c} category="payment_qr_2" title="Send QR (2nd Barcode)" color="var(--cb-c026d3)" icon={QrCode} />)}
            {kind === 'catv' && withCaption('Portal link', <PortalLinkButton customer={c} />)}
          </div>
        )}
      </div>
    );
  };
  if (rows.length === 0) return null; // an empty service takes no space; the page shows one empty note if both are empty
  return (
    <div className="card" style={{ marginBottom: isApp ? 10 : 16 }}>
      <div className="card-header" style={isApp ? { padding: '8px 12px' } : undefined}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={16} color={color} /> {title}
        </h3>
        <span className="badge badge-info">{rows.length} customers</span>
      </div>
      <div className="table-container">
        {rows.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={28} color="var(--success)" />
            <h3>No customers here</h3>
            <p>All good in this category!</p>
          </div>
        ) : isApp ? (
          <div>{rows.map(appCard)}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{kind === 'broadband' ? 'Username' : 'Account No.'}</th>
                <th>Customer</th>
                <th>{kind === 'broadband' ? 'Plan' : 'Package'}</th>
                <th>Expiry</th>
                <th>Balance</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c._id}>
                  <td>
                    <Link to={`/customers/${c._id}`} style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '.88rem', textDecoration: 'none' }}>
                      {(kind === 'broadband' ? c.broadband?.username : c.catv?.accountNumber) || '—'}
                    </Link>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Phone size={10} /> {c.mobile}
                    </div>
                    {c.address?.line1 && (
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                        <MapPin size={10} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{c.address.line1}</span>
                      </div>
                    )}
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{c.customerId}</div>
                    <LatestNoteLine note={c.renewalNote} />
                  </td>
                  <td style={{ fontSize: '.82rem' }}>
                    {(kind === 'broadband' ? c.broadband?.plan?.name : c.catv?.package?.name) || '—'}
                    <ValidityLine service={kind === 'broadband' ? c.broadband : c.catv} />
                  </td>
                  <td><ExpiryChip date={kind === 'broadband' ? c.broadband?.expiryDate : c.catv?.expiryDate} /></td>
                  <td>
                    <span style={{ fontWeight: 600, fontSize: '.82rem', color: c.outstandingBalance > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                      ₹{fmt(c.outstandingBalance)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button className="btn btn-sm" title="Call notes / follow-up reminder" onClick={() => setNoteFor(c)}
                        style={{ background: 'var(--cb-ca8a04)', borderColor: 'var(--cb-ca8a04)', color: 'var(--c-ffffff)', padding: '3px 8px' }}>
                        <StickyNote size={13} />
                      </button>
                      <WaDropdown customer={c} templates={waTemplates} />
                      <TemplateActionButton customer={c} category="call_no_answer" title="Call Couldn't Connect" color="var(--cb-ea580c)" icon={PhoneMissed} />
                      <TemplateActionButton customer={c} category="plans" title="Send Plans" color="var(--cb-0d9488)" icon={Package} />
                      <TemplateActionButton customer={c} category="payment_qr" title="Send QR" color="var(--cb-111827)" icon={QrCode} />
                      {kind === 'broadband' && (
                        <TemplateActionButton customer={c} category="payment_qr_2" title="Send QR (2nd Barcode)" color="var(--cb-c026d3)" icon={QrCode} />
                      )}
                      {kind === 'catv' && <PortalLinkButton customer={c} />}
                      <Link to={`/customers/${c._id}?tab=collect`} className="btn btn-primary btn-sm" title="Renew">
                        <RefreshCw size={12} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {noteFor && (
        <RenewalNoteModal customer={noteFor} kind={kind} onClose={() => setNoteFor(null)}
          onAdded={note => onNoteAdded(kind, noteFor._id, note)} />
      )}
    </div>
  );
}

export default function RenewalsPage() {
  useDarkReady();
  const isApp = useViewMode().viewMode === 'app';
  const [tab, setTab] = useState('today');
  const [bbData, setBbData] = useState([]);
  const [catvData, setCatvData] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Date filter: when either date is set the page lists everyone whose expiry falls
  // in the range (past or future), overriding the Today / 7 Days / Expired tabs.
  const [showDates, setShowDates] = useState(false); // App view: date range stays folded until asked for
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const rangeActive = Boolean(dateFrom || dateTo);
  const [waTemplates, setWaTemplates] = useState(DEFAULT_TEMPLATES);

  useEffect(() => {
    renewalAPI.getDashboard()
      .then(({ data: d }) => setDashStats(d.data))
      .catch(() => {});
    settingsAPI.getBilling()
      .then(({ data: d }) => {
        const tpls = d.data?.whatsappTemplates;
        if (tpls && tpls.length > 0) setWaTemplates(tpls);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) return;
    setLoading(true);
    const days = tab === 'today' ? 0 : tab === '7days' ? 7 : null;
    const range = { from: dateFrom || undefined, to: dateTo || undefined };
    const [bbFetch, catvFetch] = rangeActive
      ? [renewalAPI.getRange({ ...range, serviceType: 'broadband' }), renewalAPI.getRange({ ...range, serviceType: 'catv' })]
      : tab === 'expired'
      ? [renewalAPI.getExpired({ serviceType: 'broadband' }), renewalAPI.getExpired({ serviceType: 'catv' })]
      : [renewalAPI.getUpcoming({ days, serviceType: 'broadband' }), renewalAPI.getUpcoming({ days, serviceType: 'catv', ...(tab === '7days' && { excludeRegular: 1 }) })];

    Promise.all([bbFetch, catvFetch])
      .then(([bb, catv]) => { setBbData(bb.data.data); setCatvData(catv.data.data); })
      .catch(() => toast.error('Failed to load renewals'))
      .finally(() => setLoading(false));
  }, [tab, dateFrom, dateTo, rangeActive]);

  const statCards = dashStats ? [
    { label: 'Expiring Today', short: 'Today', value: dashStats.today, color: 'var(--c-dc2626)', bg: 'var(--cb-fee2e2)', icon: AlertTriangle, key: 'today' },
    { label: 'Within 7 Days', short: 'In 7 days', value: dashStats.in7days, color: 'var(--primary)', bg: 'var(--cb-dbeafe)', icon: RefreshCw, key: '7days' },
    { label: 'Already Expired', short: 'Expired', value: dashStats.expired, color: 'var(--c-64748b)', bg: 'var(--cb-f1f5f9)', icon: XCircle, key: 'expired' },
  ] : [];

  const matches = (c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.mobile?.includes(search) ||
      c.customerId?.toLowerCase().includes(q) ||
      c.broadband?.username?.toLowerCase().includes(q) ||
      c.catv?.accountNumber?.toLowerCase().includes(q) ||
      c.address?.line1?.toLowerCase().includes(q) ||
      c.address?.area?.toLowerCase().includes(q)
    );
  };
  const handleNoteAdded = (kind, customerId, note) => {
    const setter = kind === 'broadband' ? setBbData : setCatvData;
    setter(rows => rows.map(r => (r._id === customerId ? { ...r, renewalNote: note } : r)));
  };

  const filteredBb = bbData.filter(matches);
  const filteredCatv = catvData.filter(matches);

  return (
    <div>
      {!isApp && (
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Renewal Dashboard</h2>
          <p className="text-muted text-small">Track expiring and expired subscriptions</p>
        </div>
      </div>
      )}

      {/* Stat cards - clickable */}
      {isApp ? (
        // App view: one slim segmented control (number + label side by side); same click behaviour.
        <div role="tablist" style={{ display: 'flex', gap: 2, padding: 3, marginBottom: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>
          {statCards.map(c => {
            const on = !rangeActive && tab === c.key;
            return (
              <button key={c.key} type="button" role="tab" aria-selected={on}
                onClick={() => { setTab(c.key); setDateFrom(''); setDateTo(''); }}
                style={{ flex: 1, border: 0, background: on ? c.bg : 'transparent', borderRadius: 9, padding: '7px 2px', cursor: 'pointer', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 5, minWidth: 0 }}>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: c.color, lineHeight: 1 }}><Num value={c.value} /></span>
                <span style={{ fontSize: '.7rem', color: on ? c.color : 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.short}</span>
              </button>
            );
          })}
        </div>
      ) : (
      <div className="stats-grid mb-6">
        {statCards.map(c => (
          <div key={c.key} className="stat-card" style={{ cursor: 'pointer', border: !rangeActive && tab === c.key ? `2px solid ${c.color}` : '1px solid var(--border)' }}
            onClick={() => { setTab(c.key); setDateFrom(''); setDateTo(''); }}>
            <div className="stat-icon" style={{ background: c.bg }}>
              <c.icon size={20} color={c.color} />
            </div>
            <div className="stat-info">
              <p>{c.label}</p>
              <h3><Num value={c.value} /></h3>
              <div className="trend" style={{ color: c.color }}>Click to view</div>
            </div>
          </div>
        ))}
      </div>
      )}

      <div className={isApp ? undefined : 'flex justify-between items-center mb-4'} style={isApp ? { display: 'grid', gap: 8, marginBottom: 10 } : undefined}>
        <h3 style={isApp ? { margin: 0, fontSize: '.8rem', color: 'var(--text-muted)', fontWeight: 600 } : { margin: 0 }}>
          {rangeActive && `Expiry ${dateFrom ? format(new Date(dateFrom), 'dd/MM/yyyy') : '…'} – ${dateTo ? format(new Date(dateTo), 'dd/MM/yyyy') : '…'}`}
          {!rangeActive && tab === 'today' && 'Expiring Today'}
          {!rangeActive && tab === '7days' && 'Expiring Within 7 Days'}
          {!rangeActive && tab === 'expired' && 'Expired Subscriptions'}
        </h3>
        <div style={isApp ? { display: 'flex', flexDirection: 'column-reverse', gap: 8 } : { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isApp && !showDates && !rangeActive ? null : (
          <div style={isApp ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '.76rem' } : { display: 'flex', gap: 6, alignItems: 'center', fontSize: '.82rem' }}>
            <label style={isApp ? { display: 'grid', gap: 4, minWidth: 0 } : { display: 'contents' }}>
              <span className="text-muted">Expiry from</span>
              <input type="date" className="form-control" style={isApp ? { width: '100%', minWidth: 0 } : { width: 145, padding: '4px 8px' }}
                value={dateFrom} max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)} />
            </label>
            <label style={isApp ? { display: 'grid', gap: 4, minWidth: 0 } : { display: 'contents' }}>
              <span className="text-muted">to</span>
              <input type="date" className="form-control" style={isApp ? { width: '100%', minWidth: 0 } : { width: 145, padding: '4px 8px' }}
                value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} />
            </label>
            {rangeActive && (
              <button className="btn btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
            )}
          </div>
          )}
          <div style={isApp ? { display: 'flex', gap: 8 } : { display: 'contents' }}>
            <div className="search-input-wrapper" style={isApp ? { flex: 1, minWidth: 0 } : undefined}>
              <Search size={13} className="search-icon" />
              <input className="search-input" style={{ width: isApp ? '100%' : 220 }}
                placeholder="Name, mobile, account..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {isApp && (
              <button type="button" className="btn btn-secondary" aria-pressed={showDates || rangeActive} title="Filter by expiry date"
                onClick={() => setShowDates(s => !s)} style={{ minHeight: 40, padding: '0 12px' }}>
                <CalendarDays size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <>
          {filteredBb.length + filteredCatv.length === 0 && (
            <div className="empty-state" style={isApp ? { padding: '24px 0' } : undefined}>
              <CheckCircle size={28} color="var(--success)" />
              <h3>No customers here</h3>
              <p>All good in this category!</p>
            </div>
          )}
          <ServiceSection icon={Wifi} color="var(--accent)" title="Broadband" rows={filteredBb} kind="broadband" waTemplates={waTemplates} onNoteAdded={handleNoteAdded} />
          <ServiceSection icon={Tv} color="var(--primary)" title="CableTV" rows={filteredCatv} kind="catv" waTemplates={waTemplates} onNoteAdded={handleNoteAdded} />
        </>
      )}
    </div>
  );
}
